import { useEffect, useState, type CSSProperties } from 'react';
import MuxPlayer from '@mux/mux-player-react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { cn } from '@/lib/utils';
import type { MediaFileDTO } from '@/api/generated/model';
import { isVideoMedia } from './media';
import { safeMediaSrc } from '@/lib/mediaUrl';

interface MediaLightboxProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  medias: MediaFileDTO[];
  initialIndex?: number;
}

const muxContainStyle = {
  width: '100%',
  height: '100%',
  maxHeight: '100%',
  '--media-object-fit': 'contain',
} as CSSProperties;

// Port of MediaViewerDialog (doc 08 §2.2 / §4.7): video -> mux HLS player (or <video> for MP4);
// images -> multi-image gallery with counter, wrap-around prev/next, and a thumbnail strip.
export function MediaLightbox({ open, onOpenChange, medias, initialIndex = 0 }: MediaLightboxProps) {
  const [index, setIndex] = useState(initialIndex);

  useEffect(() => {
    if (open) setIndex(initialIndex);
  }, [open, initialIndex]);

  useEffect(() => {
    if (!open || medias.length <= 1) return;

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        setIndex((i) => (i - 1 + medias.length) % medias.length);
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        setIndex((i) => (i + 1) % medias.length);
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [open, medias.length]);

  if (medias.length === 0) return null;
  const safeIndex = Math.min(index, medias.length - 1);
  const current = medias[safeIndex];
  const url = current?.url ?? '';
  const isVideo = current ? isVideoMedia(current) : false;
  const isHls = url.includes('.m3u8');

  const prev = () => setIndex((i) => (i - 1 + medias.length) % medias.length);
  const next = () => setIndex((i) => (i + 1) % medias.length);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[90vw] border-0 bg-black p-6 text-white sm:max-w-3xl">
        <DialogTitle className="sr-only">Media viewer</DialogTitle>

        {/* Fixed viewport height so portrait Mux/native video cannot grow past the dialog. */}
        <div className="relative flex h-[70vh] items-center justify-center overflow-hidden">
          {isVideo ? (
            isHls ? (
              <MuxPlayer
                streamType="on-demand"
                src={url}
                style={muxContainStyle}
                accentColor="#0194F5"
              />
            ) : (
              // Direct MP4 (non-HLS) falls back to the native player.
              <video
                src={url}
                controls
                playsInline
                className="max-h-full max-w-full object-contain"
              />
            )
          ) : (
            <img
              src={safeMediaSrc(url)}
              alt=""
              className="max-h-full max-w-full object-contain"
            />
          )}

          {medias.length > 1 && (
            <>
              <button
                type="button"
                onClick={prev}
                className="absolute left-2 rounded-full bg-black/50 p-1 text-white hover:bg-black/70"
                aria-label="Previous"
              >
                <ChevronLeft className="size-5" />
              </button>
              <button
                type="button"
                onClick={next}
                className="absolute right-2 rounded-full bg-black/50 p-1 text-white hover:bg-black/70"
                aria-label="Next"
              >
                <ChevronRight className="size-5" />
              </button>
            </>
          )}
        </div>

        {medias.length > 1 && (
          <>
            <div className="text-center text-xs text-white/60">
              {safeIndex + 1} / {medias.length}
            </div>
            <div className="flex gap-2 overflow-x-auto">
              {medias.map((m, i) => (
                <button
                  key={`${m.url ?? 'm'}-${i}`}
                  type="button"
                  onClick={() => setIndex(i)}
                  className={cn(
                    'size-14 shrink-0 overflow-hidden rounded border',
                    i === safeIndex ? 'border-primary' : 'border-transparent',
                  )}
                >
                  {isVideoMedia(m) ? (
                    <div className="flex size-full items-center justify-center bg-muted text-[10px] text-muted-foreground">
                      Video
                    </div>
                  ) : (
                    <img src={safeMediaSrc(m.url)} alt="" className="size-full object-cover" />
                  )}
                </button>
              ))}
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
